import { useEffect } from "react";

import { useRealTimeTypedQuery, useSelectedLocation } from "components";
import { useLocationServicesQuery } from "modules/PendingChecklistsDashboard/components/ServiceChecklists/hooks";
import { useCheckInServiceChecklistsListeners } from "modules/PendingChecklistsDashboard/components/ServiceChecklists/listeners";
import { PendingChecklistsQueryKeys } from "modules/PendingChecklistsDashboard/queryKeys";
import { ApiInstance } from "utils/Api";

import type { CheckInService, CheckInServicePendingChecklist } from "models";

export type ServicePendingChecklistsGrouped = {
  name: string;
  checklists: CheckInServicePendingChecklist[];
  checkInServiceId: number;
};

const handleSelectServiceChecklists = (servicePendingChecklists: CheckInServicePendingChecklist[], locationServices: CheckInService[]) => {
  const servicePendingChecklistsGrouped: ServicePendingChecklistsGrouped[] = locationServices.map(({ name, id }) => ({ name, checkInServiceId: id, checklists: [] }));

  servicePendingChecklists.forEach(ch => {
    const existingGroup = servicePendingChecklistsGrouped.find(ds => ds.checkInServiceId === ch.checkin_service_id);
    if (existingGroup) existingGroup.checklists.push(ch);
  });

  return servicePendingChecklistsGrouped.map(group => ({ ...group, checklists: group.checklists.sort((a, b) => a.ordering - b.ordering) }));
};

export const useServicePendingChecklistsQuery = () => {
  const listeners = useCheckInServiceChecklistsListeners();
  const { selectedLocation } = useSelectedLocation();
  const { data: locationServices } = useLocationServicesQuery();

  const query = useRealTimeTypedQuery({
    queryKey: PendingChecklistsQueryKeys.servicePendingChecklists,
    queryFn: async ({ endpoint, baseUrl }) =>
      (await ApiInstance.post(endpoint, { dealer_location_id: selectedLocation?.id }, baseUrl)).data.checkin_service_pending_checklists,
    select: servicePendingChecklists => handleSelectServiceChecklists(servicePendingChecklists, locationServices || []),
    enabled: false,
    listeners
  });

  useEffect(() => {
    if (selectedLocation?.id || locationServices) query.refetch();
  }, [selectedLocation?.id, locationServices]);

  return query;
};
